import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister';
import type { DehydratedState, Query, DehydrateOptions } from '@tanstack/react-query';

/**
 * Ключи кэша, которые безопасно персистить в localStorage.
 *
 * Критерии включения:
 * - Данные редко меняются (конфиг, права, флаги)
 * - Нет риска показать устаревшие бизнес-данные пользователю
 * - Блокируют рендер при отсутствии (вызывают loading-waterfall)
 *
 * НЕ персистим managerSalesPlans — данные слишком большие для localStorage (5 MB лимит).
 */
const PERSIST_QUERY_KEY_PREFIXES: ReadonlyArray<string> = [
  'feature-flags',
  'rbac',
  'ui-config',
];

/**
 * Фильтр для dehydrate — определяет, какие запросы попадают в localStorage.
 * Персистим только успешно завершённые запросы из белого списка.
 */
export function shouldPersistQuery(query: Query): boolean {
  const [firstKey] = query.queryKey as [string, ...unknown[]];
  if (typeof firstKey !== 'string') return false;
  if (query.state.status !== 'success') return false;

  return PERSIST_QUERY_KEY_PREFIXES.some(
    (prefix) => firstKey === prefix || firstKey.startsWith(`${prefix}-`) || firstKey.startsWith(`${prefix},`),
  );
}

/**
 * Версия кэша. Изменить при breaking-change в структуре данных,
 * чтобы сбросить устаревший localStorage у всех пользователей.
 */
export const CACHE_BUSTER = 'v1';

/**
 * Максимальный возраст персистентного кэша — 24 часа.
 * По истечении кэш сбрасывается автоматически.
 */
export const CACHE_MAX_AGE_MS = 24 * 60 * 60 * 1000;

/**
 * Функция-фильтр для dehydrateOptions — определяет, какие запросы
 * попадают в снимок состояния при персистировании.
 */
export const shouldDehydrateQuery: NonNullable<DehydrateOptions['shouldDehydrateQuery']> = (query) => {
  return shouldPersistQuery(query as unknown as Query);
};

/**
 * Синхронный persister на основе localStorage.
 * Throttle 2000ms — снижает нагрузку на запись при активной работе с таблицами.
 */
export const PERSIST_STORAGE_KEY = 'plng_rq_cache';

export const localStoragePersister = createSyncStoragePersister({
  storage: typeof window !== 'undefined' ? window.localStorage : null,
  key: PERSIST_STORAGE_KEY,
  throttleTime: 2000,
});

/**
 * Сбрасывает персистентный кэш.
 *
 * Обязателен при входе в эмуляцию и выходе из неё: в белом списке выше лежит
 * префикс 'rbac', то есть права пользователя, а localStorage переживает
 * window.location.reload(). Без сброса после переключения личности
 * восстанавливаются права предыдущей (см. features/impersonation).
 */
export function clearPersistedQueryCache(): void {
  try {
    localStorage.removeItem(PERSIST_STORAGE_KEY);
  } catch { /* private mode / quota */ }
}

/**
 * Типизированный хелпер для проверки валидности dehydrated state из localStorage.
 * Используется при восстановлении кэша для защиты от битых данных.
 */
export function isValidDehydratedState(value: unknown): value is DehydratedState {
  return (
    typeof value === 'object' &&
    value !== null &&
    'queries' in value &&
    Array.isArray((value as DehydratedState).queries)
  );
}
